import React from 'react';
import styled from 'styled-components';
import { IconAlertTriangle, IconBuildingBank, IconCreditCard } from '@tabler/icons-react';
import { BasketButtonLineItem } from './BasketButtonLineItem';
import { capitalize } from 'utils';
import { STRIPE_SEPA_DEBIT, STRIPE_CARD, requiresStripePaymentMethod } from 'constants/organization';
import { useOrganizationBillingInfo } from 'data/hooks/useOrganizationBillingInfo';
import { OrganizationPaymentType } from 'data/__generated__';

interface Card {
    id: string;
    type?: string | null;
    brand?: string | null;
    last4?: string | null;
    card?:
        | {
              brand?: string | null;
              last4?: string | null;
          }
        | null
        | undefined;
}

interface Organization {
    cards: Card[];
    paymentType?: string | null;
}

interface BasketPaymentInfosProps {
    organization: Organization | null;
    setIsBillingAsideOpen: (open: boolean) => void;
}

export const BasketPaymentInfos: React.FC<BasketPaymentInfosProps> = ({ organization, setIsBillingAsideOpen }) => {
    const ICON_SIZE = '18px';
    const { billingInfo, billingInfoError } = useOrganizationBillingInfo();

    if (!organization) {
        return null;
    }

    // Odoo indisponible : on l'annonce ici sans bloquer le panier. Le détail (et le bouton « Réessayer »)
    // vit dans le volet facturation.
    if (billingInfoError) {
        return (
            <PaymentInfosContainer>
                <BasketButtonLineItem
                    icon={<IconAlertTriangle size={ICON_SIZE} />}
                    label="Informations de facturation momentanément indisponibles"
                    actionLabel={null}
                    onClick={() => setIsBillingAsideOpen(true)}
                    borderBottom
                />
            </PaymentInfosContainer>
        );
    }

    const { cards, paymentType } = organization;
    const defaultSourceObject = cards.find((card) => card.id === billingInfo?.defaultPaymentMethod);
    const needsPaymentMethod = requiresStripePaymentMethod(paymentType);
    const isOdooTransfer = paymentType === OrganizationPaymentType.Odoo;
    const isSepaDebit = defaultSourceObject?.type === STRIPE_SEPA_DEBIT;
    const isCard = defaultSourceObject?.type === STRIPE_CARD;

    // Totem ne prélève pas (Odoo encaisse, ou personne) : on nomme le mode d'encaissement et on ne
    // propose rien à ajouter. Sinon : le moyen de paiement enregistré, ou l'appel à en saisir un.
    const { icon, label } = !needsPaymentMethod
        ? {
              icon: isOdooTransfer ? <IconBuildingBank size={ICON_SIZE} /> : undefined,
              label: isOdooTransfer ? 'Virement bancaire' : 'Aucun paiement requis',
          }
        : {
              icon: isSepaDebit ? <IconBuildingBank size={ICON_SIZE} /> : <IconCreditCard size={ICON_SIZE} />,
              label: billingInfo?.defaultPaymentMethod
                  ? isSepaDebit
                      ? 'SEPA (Virement bancaire)'
                      : isCard &&
                        `${capitalize(
                            defaultSourceObject?.card?.brand || defaultSourceObject?.brand || 'carte bancaire',
                        )}`
                  : 'Informations de facturation',
          };
    const isMissingPaymentMethod = needsPaymentMethod && !billingInfo?.defaultPaymentMethod;
    return (
        <PaymentInfosContainer>
            <BasketButtonLineItem
                icon={icon}
                label={label}
                actionLabel={
                    isMissingPaymentMethod
                        ? 'Ajouter'
                        : isCard &&
                          `• • • • • • • • • • • • ${defaultSourceObject?.card?.last4 || defaultSourceObject?.last4}`
                }
                bold={isMissingPaymentMethod}
                onClick={() => setIsBillingAsideOpen(true)}
                borderBottom
            />
        </PaymentInfosContainer>
    );
};

const PaymentInfosContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    cursor: pointer;
    border-width: 0 0 0.1rem;
`;
